import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, FileText, Image as ImageIcon, Users, ShoppingBag, Star,
  Mail, Inbox, Settings as SettingsIcon, Menu, Loader2, Send, Download, CreditCard, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const adminNav = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Bookings", to: "/admin/bookings", icon: Calendar },
  { label: "Email Log", to: "/admin/emails", icon: Send },
  { label: "Stripe Sync", to: "/admin/stripe/sync", icon: RefreshCw },
  { label: "Stripe Events", to: "/admin/stripe/events", icon: CreditCard },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Gallery", to: "/admin/gallery", icon: ImageIcon },
  { label: "Team", to: "/admin/team", icon: Users },
  { label: "Products", to: "/admin/products", icon: ShoppingBag },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Subscribers", to: "/admin/subscribers", icon: Mail },
  { label: "Contact", to: "/admin/contact", icon: Inbox },
  { label: "Settings", to: "/admin/settings", icon: SettingsIcon },
  { label: "Import from WP", to: "/admin/wp-import", icon: Download },
];

function Sidebar({ open, toggle }: { open: boolean; toggle: () => void }) {
  const location = useLocation();
  return (
    <>
      <button onClick={toggle} className="lg:hidden fixed top-20 left-4 z-40 bg-panel border border-border p-2">
        <Menu className="w-5 h-5" />
      </button>
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-surface border-r border-border z-30 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav className="p-4 space-y-1 mt-4">
          {adminNav.map((item) => (
            <Link key={item.to} to={item.to}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-body transition-colors ${
                location.pathname === item.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-panel"
              }`}>
              <item.icon className="w-4 h-4" />{item.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
}

type SyncCardProps = {
  title: string;
  description: string;
  fnName: string;
  body?: Record<string, unknown>;
  extra?: React.ReactNode;
};

function SyncCard({ title, description, fnName, body, extra }: SyncCardProps) {
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [result, setResult] = useState<unknown>(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(fnName, { body: body ?? {} });
      if (error) throw error;
      setResult(data);
      setLastRun(new Date().toLocaleString());
      toast.success(`${title} complete`);
    } catch (e) {
      toast.error((e as Error).message);
      setResult({ error: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-panel border border-border p-6 space-y-4">
      <div>
        <h2 className="font-display tracking-widest mb-1">{title}</h2>
        <p className="text-sm text-muted-foreground font-body">{description}</p>
      </div>
      {extra}
      <div className="flex items-center gap-3">
        <Button onClick={run} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Run sync
        </Button>
        {lastRun && <span className="text-xs font-mono text-muted-foreground">Last run: {lastRun}</span>}
      </div>
      {result != null && (
        <pre className="bg-surface border border-border p-4 overflow-auto max-h-80 text-[10px] font-mono">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function AdminStripeSync() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paymentsLimit, setPaymentsLimit] = useState("100");

  return (
    <div className="pt-16 min-h-screen">
      <Sidebar open={sidebarOpen} toggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="lg:ml-64 p-6 lg:p-10">
        <h1 className="text-3xl font-display tracking-widest mb-8">STRIPE SYNC</h1>
        <div className="grid grid-cols-1 gap-6">
          <SyncCard
            title="PRODUCTS & PRICES"
            description="Upsert every active local product to Stripe and create a fresh price for each. Updates stripe_product_id and the default variant's stripe_price_id."
            fnName="stripe-sync-all-products"
          />
          <SyncCard
            title="RECENT PAYMENTS"
            description="Pull the most recent payment intents and checkout sessions from Stripe and reconcile them with local bookings (payment_status, amount_paid)."
            fnName="stripe-sync-payments"
            body={{ limit: Number(paymentsLimit) }}
            extra={
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground font-mono uppercase text-xs">Lookback</span>
                <Select value={paymentsLimit} onValueChange={setPaymentsLimit}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50 latest</SelectItem>
                    <SelectItem value="100">100 latest</SelectItem>
                    <SelectItem value="250">250 latest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}